import React, { useEffect, useState } from "react";
import { cn } from "./cn";
import { Menu, MenuItem } from "./Menu";
import { Skeleton } from "./Skeleton";
import { Spinner } from "./Spinner";

export interface Column<T> {
  key: string;
  header: string;
  /** Sem render, mostra row[key]. */
  render?: (row: T) => React.ReactNode;
  /** Vira o título do card no celular. Sem nenhuma marcada, usa a primeira coluna. */
  primary?: boolean;
  hideOnMobile?: boolean;
  align?: "left" | "right";
}

type RowId = string | number;

interface DataTableProps<T> {
  columns: Column<T>[];
  rows: T[];
  getRowId: (row: T) => RowId;
  actions?: (row: T) => MenuItem[];
  /** Nome da linha para leitores de tela ("Ações para …"). */
  getRowLabel?: (row: T) => string;
  onRowClick?: (row: T) => void;
  /** true sem linhas = skeleton; true com linhas = recarregando (esmaece). */
  loading?: boolean;
  empty?: React.ReactNode;
  busyRowId?: RowId | null;
}

const SKELETON_ROWS = 5;
// Respostas rápidas não chegam a mostrar o skeleton — evita o "pisca" cinza
// a cada troca de seção. Até lá o espaço fica reservado (invisível).
const SKELETON_DELAY_MS = 300;

function useDelayedFlag(flag: boolean, delay: number): boolean {
  const [delayed, setDelayed] = useState(false);
  useEffect(() => {
    if (!flag) {
      setDelayed(false);
      return;
    }
    const id = setTimeout(() => setDelayed(true), delay);
    return () => clearTimeout(id);
  }, [flag, delay]);
  return flag && delayed;
}

const cellValue = <T,>(column: Column<T>, row: T): React.ReactNode => {
  if (column.render) return column.render(row);
  const value = (row as unknown as Record<string, React.ReactNode>)[column.key];
  return value ?? "—";
};

// Uma config de colunas, duas visões: tabela a partir de 768px, cards abaixo.
export function DataTable<T>({
  columns,
  rows,
  getRowId,
  actions,
  getRowLabel,
  onRowClick,
  loading = false,
  empty,
  busyRowId,
}: DataTableProps<T>) {
  const showSkeleton = useDelayedFlag(loading && rows.length === 0, SKELETON_DELAY_MS);

  if (loading && rows.length === 0) {
    return (
      <div aria-busy="true" className={cn("flex flex-col gap-2", !showSkeleton && "invisible")}>
        <span className="sr-only">Carregando…</span>
        {Array.from({ length: SKELETON_ROWS }, (_, i) => (
          <Skeleton key={i} className="h-14 w-full" />
        ))}
      </div>
    );
  }

  if (rows.length === 0) return <>{empty}</>;

  const primaryColumn = columns.find((c) => c.primary) ?? columns[0];
  const detailColumns = columns.filter((c) => c !== primaryColumn && !c.hideOnMobile);

  const renderActions = (row: T) => {
    const id = getRowId(row);
    if (busyRowId === id) {
      return (
        <span className="flex h-9 w-9 items-center justify-center text-fg-muted">
          <Spinner />
        </span>
      );
    }
    if (!actions) return null;
    return <Menu items={actions(row)} label={`Ações para ${getRowLabel?.(row) ?? id}`} />;
  };

  const clickableProps = (row: T) =>
    onRowClick
      ? {
          tabIndex: 0,
          onClick: () => onRowClick(row),
          onKeyDown: (e: React.KeyboardEvent) => {
            if (e.key === "Enter" && e.target === e.currentTarget) onRowClick(row);
          },
        }
      : {};

  return (
    <div className={cn("transition-opacity", loading && "opacity-60")} aria-busy={loading || undefined}>
      {/* Desktop / tablet */}
      <div className="hidden overflow-x-auto rounded-card border border-line bg-surface md:block">
        <table className="w-full border-collapse text-body">
          <thead>
            <tr className="bg-surface-muted">
              {columns.map((column) => (
                <th
                  key={column.key}
                  scope="col"
                  className={cn(
                    "px-4 py-3 text-caption font-semibold uppercase tracking-wide text-fg-muted",
                    column.align === "right" ? "text-right" : "text-left"
                  )}
                >
                  {column.header}
                </th>
              ))}
              {actions && (
                <th scope="col" className="w-14 px-2 py-3">
                  <span className="sr-only">Ações</span>
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const id = getRowId(row);
              return (
                <tr
                  key={id}
                  {...clickableProps(row)}
                  className={cn(
                    "border-t border-line align-middle transition-colors",
                    onRowClick &&
                      "cursor-pointer hover:bg-surface-hover focus-visible:bg-surface-hover focus-visible:outline-none",
                    busyRowId === id && "opacity-50"
                  )}
                >
                  {columns.map((column) => (
                    <td
                      key={column.key}
                      className={cn("px-4 py-3 text-fg", column.align === "right" && "text-right")}
                    >
                      {cellValue(column, row)}
                    </td>
                  ))}
                  {actions && <td className="px-2 py-2 text-right">{renderActions(row)}</td>}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Celular */}
      <ul className="flex flex-col gap-2 md:hidden">
        {rows.map((row) => {
          const id = getRowId(row);
          return (
            <li
              key={id}
              {...clickableProps(row)}
              className={cn(
                "rounded-card border border-line bg-surface p-4",
                onRowClick && "cursor-pointer active:bg-surface-hover focus-visible:outline-none focus-visible:shadow-focus",
                busyRowId === id && "opacity-50"
              )}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1 text-body text-fg">{cellValue(primaryColumn, row)}</div>
                <div className="-mr-2 -mt-2">{renderActions(row)}</div>
              </div>
              {detailColumns.length > 0 && (
                <dl className="mt-3 flex flex-col gap-2">
                  {detailColumns.map((column) => (
                    <div key={column.key} className="flex items-start justify-between gap-3 text-caption">
                      <dt className="shrink-0 text-fg-muted">{column.header}</dt>
                      <dd className="min-w-0 text-right text-fg">{cellValue(column, row)}</dd>
                    </div>
                  ))}
                </dl>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
